/**
 * «Мои сборы» и заявка из нескольких разовых мест (BLUEPRINT Stage 17, ADR-35).
 * Значения по умолчанию для `Setting`: боевые живут в таблице под ключами `SETTING_KEYS.booking*`
 * и `SETTING_KEYS.pricingSingle*`; здесь только дефолты для сида и фолбэка.
 */
export const BOOKING_DEFAULTS = {
  /** Цена разового места = доля сезона × этот множитель (`pricing.singleRatio`) */
  singleRatio: 1.3,
  /** Сколько дней после оплаты разовые места засчитываются в сезон (`pricing.singleCreditDays`) */
  singleCreditDays: 14,
  /** Сколько минут держится бронь заявки до оплаты (`booking.holdMinutes`) */
  holdMinutes: 15,
  /** Сколько позиций в одной заявке (`booking.maxItems`) */
  maxItems: 5,
  /** Сколько разовых позиций без сезона в скользящем окне (`booking.noSeasonMax`) */
  noSeasonMax: 3,
  /** Длина скользящего окна лимита без сезона, дней (`booking.noSeasonWindowDays`) */
  noSeasonWindowDays: 42,
  /** Доля мест сбора под разовые и пробные (`booking.singleShareMax`); замена не входит */
  singleShareMax: 0.25,
  /** Место, открытое на сезон, продаётся разовым только на сбор ближе стольких дней (`booking.singleSaleDays`) */
  singleSaleDays: 7,
  /** Отмена позиции с полным возвратом — раньше стольких часов до начала (`booking.cancelFullRefundHours`) */
  cancelFullRefundHours: 24,
} as const;

export type BookingDefaults = typeof BOOKING_DEFAULTS;

/**
 * Жёсткие границы контракта, не настройки: сколько позиций принимает схема `BookingInput`
 * независимо от `booking.maxItems` (сервер проверяет настройку отдельно).
 */
export const BOOKING_LIMITS = {
  itemsHardMax: 10,
  /**
   * Сколько ближайших сборов одного отряда витрина показывает разовыми местами (задача 7):
   * иначе `/open-seats` забивает расписание одного отряда, как было бы и с пробными
   */
  showcasePerSquad: 3,
} as const;

/**
 * Причины закрытия заявки и позиции в `Order.refundReason` (Stage 17, задачи 2–3). Живут в shared,
 * потому что их читают и `bookings` (держит ли позиция место), и `orders` (путь денег), и хук
 * инвариантов, а `orders` не может импортировать `bookings` без цикла.
 */
export const BOOKING_CLOSE = {
  /** Бронь не оплачена в срок (`booking.holdMinutes`) */
  holdExpired: 'hold_expired',
  /** Человек оформил другой набор позиций — прежняя живая бронь снята */
  replaced: 'booking_replaced',
  /** Человек выписался с оплаченной позиции: деньги ждут возврата ops («к возврату») */
  itemCancelled: 'item_cancelled',
  /** Позиция оплачена, но вручить её нельзя (место ушло, сбор отменён или прошёл) — «к возврату» */
  itemUnfulfilled: 'item_unfulfilled',
  /** Деньги пришли по уже закрытой заявке (бронь истекла или заменена) — «к возврату» целиком */
  latePayment: 'late_payment',
  /**
   * Ops отменил сбор (или сезон) с оплаченной позицией — «к возврату» на остаток (задача 5).
   * Позиция не возвращается сразу: деньги ops возвращает в кабинете Робокассы и нажимает «Возврат».
   */
  gatheringCancelled: 'gathering_cancelled',
  /**
   * Ops закрыл неоплаченную заявку в «Заказах» без своей причины (задача 6): заявка и позиции
   * `CANCELLED`, места вернулись. Не «к возврату» — денег по заявке нет; пришедшие позже —
   * поздний платёж (`late_payment`)
   */
  opsCancelled: 'ops_cancelled',
  /**
   * Раунд персон 3 (N10): человек сам снял свою неоплаченную заявку («Отменить заявку»).
   * Места возвращаются на витрину, как по сроку брони; деньги, пришедшие позже, ловит
   * поздний платёж (`late_payment`)
   */
  userCancelled: 'user_cancelled',
} as const;

export type BookingCloseReason = (typeof BOOKING_CLOSE)[keyof typeof BOOKING_CLOSE];

/**
 * Оплаченная позиция с такой причиной места не держит и записью не считается: деньги у нас,
 * но их нужно вернуть («ждут возврата», `providerPayload.refundPending`). Статус при этом
 * остаётся `PAID`, чтобы позицию принимал `refundOrder` (ADR-40 п. 2).
 */
export const BOOKING_RELEASED_REASONS: readonly string[] = [
  BOOKING_CLOSE.itemCancelled,
  BOOKING_CLOSE.itemUnfulfilled,
  BOOKING_CLOSE.latePayment,
  BOOKING_CLOSE.gatheringCancelled,
];

/** Позиция снята с места, но деньги по ней ещё не вернули или вернули не целиком. */
export function isReleasedItem(refundReason: string | null | undefined): boolean {
  return refundReason != null && BOOKING_RELEASED_REASONS.includes(refundReason);
}
